import { Pagination, SortedSide, Template } from '@ekkogmbh/apisdk';
import { Box, MenuItem } from '@mui/material';
import { Add, Delete, Edit, Highlight } from '@mui/icons-material';
import { useCallback, useMemo, useState } from 'react';
import { GenericDialog } from '../../Common/Components/GenericDialog';
import { DataTable } from '../../Common/Components/DataTable';
import { StyledSelectField } from '../../Common/Components/Forms/StyledSelectField';
import { NodeSeparator } from '../../Common/Helper/Nodes';
import Permissions from 'src/Common/Permissions';
import { SortedSidePanel } from './SortedSidePanel';
import React from 'react';
import { ContentControl } from 'src/Common/Components/ContentControl';
import { ContentControlButton } from 'src/Common/Components/ContentControl/ContentControlButton';
import { nodeSeparator } from 'src/AreaManagement/Components/AreaManagementContent';
import useEkko from 'src/Common/Providers/EkkoProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { useContentControl } from 'src/Common/Providers/ContentControlProvider';
import useRequest from 'src/Common/Hooks/useRequest';

export const PickingSidesContent = (): React.JSX.Element => {
  const { api, hasPermission } = useEkko();
  const { setPanelOpen } = useContentControl();
  const { refresh } = usePagination();

  const [, startRequest] = useRequest();
  const [openDialog, setOpenDialog] = useState<'delete' | 'blink' | undefined>();
  const [selectedSide, setSelectedSide] = useState<SortedSide>();
  const [blinkDuration, setBlinkDuration] = useState<number>(60);

  const hasWritePermission = useMemo(() => hasPermission(Permissions.PICKING_AREAS_WRITE), [hasPermission]);

  const dismiss = useCallback(() => {
    setSelectedSide(undefined);
    setPanelOpen(false);
    setOpenDialog(undefined);
    setBlinkDuration(60);
  }, []);

  const deleteSide = useCallback(() => {
    if (selectedSide === undefined) {
      return;
    }

    startRequest(
      () => api.deleteSortedSide(selectedSide),
      () => {
        dismiss();
        refresh();
      },
    );
  }, [api, selectedSide]);

  const blinkSide = useCallback(() => {
    if (selectedSide === undefined) {
      return;
    }

    startRequest(() => api.blinkSortedSide(selectedSide, blinkDuration));
  }, [api, selectedSide, blinkDuration]);

  const fetchSortedSides = useCallback((pagination: Pagination) => api.getSortedSides(pagination), [api]);

  const dialog = useMemo(() => {
    if (openDialog === undefined || selectedSide === undefined) {
      return undefined;
    }

    switch (openDialog) {
      case 'delete':
        return (
          <GenericDialog
            open
            centered
            fullWidth
            type="confirmation"
            maxWidth={'sm'}
            title={'Delete Picking-Side'}
            text={
              <React.Fragment>
                <div>
                  Delete Picking-Side: <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedSide.name}</span>
                </div>
                <div>
                  on Node:{' '}
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{selectedSide.node.parts.join(NodeSeparator)}</span>?
                </div>
              </React.Fragment>
            }
            onClose={dismiss}
            onConfirm={deleteSide}
          />
        );
      case 'blink':
        return (
          <GenericDialog
            open
            fullWidth
            centered
            type="confirmation"
            maxWidth={'sm'}
            minHeight={170}
            title={'Blink ' + selectedSide.name}
            text={
              <Box sx={{ p: 1 }}>
                <StyledSelectField
                  value={blinkDuration}
                  onChange={(e) => {
                    setBlinkDuration(e.target.value as number);
                  }}
                  label={'Duration'}
                >
                  <MenuItem value={0}>stop</MenuItem>
                  <MenuItem value={60}>1 minute</MenuItem>
                  <MenuItem value={300}>5 minutes</MenuItem>
                  <MenuItem value={600}>10 minutes</MenuItem>
                </StyledSelectField>
              </Box>
            }
            onClose={dismiss}
            onConfirm={blinkSide}
          />
        );
      default:
        return;
    }
  }, [openDialog, selectedSide, deleteSide, blinkSide, blinkDuration]);

  return (
    <ContentControl
      panel={<SortedSidePanel sortedSide={selectedSide} closeHandler={dismiss} />}
      dialog={dialog}
      buttons={[
        <ContentControlButton
          key={'add'}
          content={<Add />}
          onClick={() => {
            dismiss();
            setSelectedSide(undefined);
            setPanelOpen(true);
          }}
          tooltip={'Add Sorted-Side'}
          requiredPermission={Permissions.PICKING_AREAS_WRITE}
        />,
      ]}
    >
      <DataTable
        fetchItems={fetchSortedSides}
        actions={[
          {
            title: 'Blink',
            icon: <Highlight />,
            onClick: (entry: SortedSide) => {
              dismiss();
              setSelectedSide(entry);
              setOpenDialog('blink');
            },
            disabled: !hasWritePermission,
          },
          {
            title: 'Edit',
            icon: <Edit />,
            onClick: (entry: SortedSide) => {
              dismiss();
              setSelectedSide(entry);
              setPanelOpen(true);
            },
            disabled: !hasWritePermission,
          },
          {
            title: 'Delete',
            icon: <Delete />,
            onClick: (entry: SortedSide) => {
              dismiss();
              setSelectedSide(entry);
              setOpenDialog('delete');
            },
            disabled: !hasWritePermission,
          },
        ]}
        columns={[
          {
            header: 'Name',
            filterField: 'name',
            sortField: 'S.name',
            body: (entry: SortedSide) => entry.name,
          },
          {
            header: 'Key',
            filterField: 'key',
            sortField: 'S.key',
            body: (entry: SortedSide) => entry.key,
          },
          {
            header: 'Templates',
            filterField: 'templates',
            sortField: 'S.templates',
            body: (entry: SortedSide) => {
              const { templates } = entry;
              const mapper = (template: Template, index: number): React.ReactNode => (
                <li key={index}>{template.name}</li>
              );

              return <ul style={{ paddingInlineStart: 16 }}>{templates && templates.map(mapper)}</ul>;
            },
          },
          {
            header: 'Area',
            body: (entry: SortedSide) => entry.node.parts.join(' ' + nodeSeparator + ' '),
          },
          {
            header: 'Sorting-Strategy',
            body: (entry: SortedSide) => entry.sortingStrategy,
            filterField: 'sortingStrategy',
            sortField: 'S.sortingStrategy',
          },
        ]}
      />
    </ContentControl>
  );
};
